import { defineStore } from 'pinia';
import {
  IBasket,
  IBasketProduct,
  IBasketRoutes,
} from '@/storePinia/stores/basket/interfaces/IBasket';
import { IBasketMessages } from '@/storePinia/stores/basket/interfaces/IBasketMessages';
import { hydrated } from '@/storePinia';
import useBasketModal from '@use/useBasketModal';
import useLoader from '@use/useLoader/useLoader';
import axios from 'axios';
import { IMessages } from '@/interfaces';
import { useResultsStore } from '@/storePinia/stores/results';
import { EXPRESS_DELIVERY_FIELDS_KEY } from '@/utils/expressDelivery';

type IBasketGetters = {
  inBasket(state: IBasket): (id: string) => boolean;
  getProductById(state: IBasket): (id: string) => undefined | IBasketProduct;
  getBasketCoupon(state: IBasket): undefined | { name: string; value: string };
  getSovaClub(state: IBasket): undefined | { name: string; value: string };
};

type ICreateOrderByOneClickReturn = {
  orderId: number;
  status: boolean;
  messages: IMessages;
  dataLayer: {
    actionField: {
      affiliation: string;
      coupon: boolean;
      id: number;
      revenue: number;
      shipping: {
        currency: string;
        isShowTax: boolean;
        price: number;
        printPrice: string;
        taxPercent: number;
      };
    };
    products: Array<{
      brand: 'Sova';
      category: 'Кольца';
      id: '29761';
      name: '110461420301';
      price: 8993;
      quantity: 1;
      variant: '15';
    }>;
  };
};

type IBasketActions = {
  add: (payload: {
    id: string | string[];
    count?: number;
    price?: number;
    isProductWithoutNominal?: boolean;
    barcode?: string;
    printWeight?: string;
    weight?: string;
    weightOption?: string;
  }) => Promise<void>;
  remove: (payload: { id: string; basketId: string }) => Promise<void>;
  update: (payload: { id: string; count?: number; basketId: number }) => Promise<void>;
  createOrderByOneClick: (requestParams: {
    id: string;
    count?: number;
    phone?: string;
    ['g-recaptcha-response']?: string;
    LANGUAGE_ID: string;
    price?: number;
    isProductWithoutNominal?: boolean;
  }) => Promise<ICreateOrderByOneClickReturn | undefined>;
  changeSKU: (payload: {
    id: string;
    count: number;
    basketId: string;
    price: number;
    isProductWithCustomWeight?: boolean;
    weightOption?: string;
    weight?: number;
    barcode?: string;
    oldId: number;
    newId: number;
    skuIBlockId: number;
  }) => Promise<IBasket | undefined>;
  getData: () => Promise<undefined>;
  setClubCard: () => Promise<void>;
  setCoupon: (payload: {
    coupon: string;
    phone: string;
  }) => Promise<IBasket | undefined | { error: string }>;
  deleteCoupon: (payload: string) => Promise<IBasket | undefined>;
  setCertificate: (payload: string) => Promise<IBasket | undefined | { error: string }>;
  deleteCertificate: (payload: string) => Promise<IBasket | undefined>;
  getBackBasketItems: () => Promise<void>;
  changeProductWithoutNominalPrice: (
    detailPage: string,
    payload: {
      price: number;
    }
  ) => Promise<unknown | undefined>;
  setData: (payload: IBasket) => void;
  changeProductWeightPrice: (
    detailPage: string,
    payload: {
      price: number;
      weightOption: string;
      weight: number;
      barcode: number;
      OFFER_ID: string;
    }
  ) => Promise<any | undefined>;
};

const loader = useLoader();

let getDataRequestId = 0;

export const useBasketStore = defineStore<'basket', IBasket, IBasketGetters, IBasketActions>(
  'basket',
  {
    state: (): IBasket => ({
      count: 0,
      delivery: {},
      guid: '',
      items: [],
      kits: [],
      kitsTabs: [],
      messages: hydrated<IBasketMessages>(),
      price: {},
      printCount: '',
      routes: hydrated<IBasketRoutes>(),
      couponStatus: {},
    }),
    getters: {
      inBasket: (state) => (id: string) => {
        try {
          if (Array.isArray(state.items)) {
            // eslint-disable-next-line eqeqeq
            return !!state.items.find((el) => el?.offer?.id == id || el?.id == id);
          }
          return false;
        } catch (err) {
          console.error(err);
          return false;
        }
      },
      getProductById: (state) => (id: string) => {
        try {
          if (Array.isArray(state.items)) {
            // eslint-disable-next-line eqeqeq
            return state.items.find((el) => el?.offer?.id == id || el?.id == id);
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        }
      },
      getBasketCoupon: (state) => {
        try {
          if (state.price?.coupons) {
            return {
              name: Object.keys(state.price.coupons)[0],
              value: Object.values(state.price.coupons)[0],
            };
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        }
      },
      getSovaClub: (state) => {
        try {
          if (state.price?.clubCard) {
            return {
              name: Object.keys(state.price.clubCard)[0],
              value: Object.values(state.price.clubCard)[0],
            };
          }
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        }
      },
    },
    actions: {
      async add(payload) {
        try {
          if (!this.routes.add) return;
          loader.start();

          const { path, 'params-example-data': params } = this.routes.add;
          const requestParams = {
            action: params.action,
            ...payload,
            count: payload.count ?? 1,
          };

          const { status, data: responseData } = (await axios.post(path, requestParams)) as {
            status: number;
            data: IBasket;
          };

          if (status !== 200) {
            throw new Error(`Bad request response status: ${status}`);
          }

          this.$patch(responseData);
          /**
           * @Dima , fix this shit
           */
          // this.$patch(responseData as any);
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async remove(payload) {
        try {
          if (!this.routes.remove) return;
          loader.start();
          const { path, 'params-example-data': params } = this.routes.remove;
          const requestParams = {
            id: payload.id,
            basket_Id: payload.basketId,
            action: params.action,
          };
          const {
            status,
            data: responseData,
          }: {
            status: number;
            data: IBasket | { error: string; redirect: string };
          } = await axios.post(path, requestParams);

          if (status !== 200) {
            throw new Error(`Bad request delete product from basket status: ${status}`);
          }

          if ('error' in responseData) {
            throw new Error(
              `Bad request delete product from basket status: ${status} \n ${responseData.error}`
            );
          }

          if (!('items' in responseData)) {
            const basketModal = useBasketModal();
            basketModal.close();
            /**
             * https://jira.aniart.com.ua/browse/SOVA2-286
             * Редирект на главную после очищения корзины от всех товаров в чекауте
             */
            if ('redirect' in responseData) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              window.location.href = responseData.redirect;
              return;
            }
          }

          this.$reset();
          this.$patch(responseData as IBasket);
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async update({ id, count = 1, basketId }) {
        try {
          if (!this.routes.update) return;
          loader.start();

          const { path, 'params-example-data': params } = this.routes.update;

          const requestParams = {
            action: params.action,
            id,
            count,
            basketId,
          };

          const { status, data: responseData } = await axios.post(path, requestParams);

          if (status !== 200) {
            throw new Error(`add to basket bad response status ${status}`);
          }

          this.$patch(responseData);
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async createOrderByOneClick(requestParams) {
        try {
          if (!this.routes?.buyOneClick) return undefined;
          loader.start();
          const { path, 'params-example-data': params } = this.routes.buyOneClick;

          const ajaxParams = {
            ...requestParams,
            action: params.action,
            LANGUAGE_ID: params.LANGUAGE_ID,
          };

          const {
            status,
            data: responseData,
          }: {
            status: number;
            data: ICreateOrderByOneClickReturn;
          } = await axios.post(path, ajaxParams);

          if (status !== 200) {
            throw new Error(`create order one click bad response status ${status}`);
          }

          await this.getData();

          return responseData;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async changeSKU(payload) {
        try {
          loader.start();

          if (!this.routes.changeSku) return undefined;

          const {
            path,
            'params-example-data': { action },
          } = this.routes.changeSku;

          const changeSkuProps: { [name: string]: any } = {
            action,
            ...payload,
          };

          const response: {
            status: string;
            data: IBasket;
          } = await axios.post(path, changeSkuProps);

          // if (response.status == 200) {
          this.$patch(response.data);
          // }

          return response.data;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async getData() {
        // eslint-disable-next-line no-plusplus
        const requestId = ++getDataRequestId;

        try {
          if (!this.routes.getBasket) return undefined;
          loader.start();

          const { path, action } = this.routes.getBasket;

          const {
            status,
            data,
          }: {
            status: number;
            data: IBasket;
          } = await axios.post(path, { action });

          if (status === 200 && requestId === getDataRequestId) {
            this.$reset();
            this.$patch(data);
          }

          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async setClubCard() {
        try {
          const {
            data: responseData,
          }: {
            status: number;
            data: {
              clubCard: string;
            };
          } = await axios.post('/ajax/', {
            action: 'setClubCard',
          });
          // situations appears when user has more than 1 clubcard
          if (responseData?.clubCard?.length > 0 && !this.price?.clubCard && this.count > 0) {
            await this.getData();
          }
          // if (status !== 200) throw `Bad request response status: ${status}`;
        } catch (err) {
          console.error(err);
        }
      },
      async setCoupon(payload) {
        try {
          loader.start();
          if (!this.routes.setCoupon) return undefined;

          const {
            path,
            'params-example-data': { action },
          } = this.routes.setCoupon;
          const props = {
            action,
            ...payload,
          };

          const {
            data,
          }:
            | { data: IBasket }
            | {
                data: {
                  error: string;
                };
              } = await axios.post(path, props);

          if (!('error' in data)) {
            this.$patch(data);
          }

          return data;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async deleteCoupon(payload) {
        try {
          loader.start();
          if (!this.routes?.delCoupon) return undefined;

          const {
            path,
            'params-example-data': { action },
          } = this.routes.delCoupon;

          const props = {
            coupon: payload,
            action,
          };

          const { data }: { data: IBasket } = await axios.post(path, props);

          this.$reset();
          this.$patch(data);

          return data;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async setCertificate(payload) {
        try {
          loader.start();
          if (!this.routes?.setCertificate) return undefined;

          const {
            path,
            'params-example-data': { action },
          } = this.routes.setCertificate;

          const props = {
            certificate: payload,
            action,
          };

          const {
            data,
            status,
          }: {
            status: number;
            data: IBasket;
          } = await axios.post(path, props);

          if (status !== 200) {
            throw new Error(`Something went wrong status ${status}`);
          }

          if (!('error' in data)) {
            this.$state = data;
          }

          return data;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async deleteCertificate(payload) {
        try {
          loader.start();
          if (!this.routes.delCertificate) return undefined;

          const {
            path,
            'params-example-data': { action },
          } = this.routes.delCertificate;
          const props = {
            certificate: payload,
            action,
          };

          const {
            data,
            status,
          }: {
            status: number;
            data: IBasket;
          } = await axios.post(path, props);

          if (status !== 200) {
            throw new Error(`Something went wrong status ${status}`);
          }

          this.$state = data;

          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async getBackBasketItems() {
        try {
          loader.start();

          const expressFieldsString = window.sessionStorage.getItem(EXPRESS_DELIVERY_FIELDS_KEY);
          if (expressFieldsString && !window.location.pathname.includes('/personal/order/make/')) {
            await axios.post('/ajax/', {
              action: 'getBackBasketItems',
            });
            window.sessionStorage.removeItem(EXPRESS_DELIVERY_FIELDS_KEY);
            await this.getData();
          }

          const createCreditOrder = sessionStorage.getItem('createCreditOrder');
          if (createCreditOrder && !window.location.pathname.includes('/personal/order/make/')) {
            await axios.post('/ajax/', {
              action: 'getBackBasketItems',
            });
            window.sessionStorage.removeItem('createCreditOrder');
            await this.getData();
          }

          const engraving = sessionStorage.getItem('engraving');
          if (engraving && !window.location.pathname.includes('/personal/order/make/')) {
            await axios.post('/ajax/', {
              action: 'getBackBasketItems',
            });
            window.sessionStorage.removeItem('engraving');
            await this.getData();
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async changeProductWithoutNominalPrice(detailPage, payload) {
        try {
          loader.start();
          const resultsStore = useResultsStore();

          const action =
            this.routes.changeProductWithoutNominalPrice?.['params-example-data']?.action;

          const pageDadaResponse = await axios.get(detailPage, {
            params: {
              api: 'Y',
              ...resultsStore.getRequestLang,
              ...payload,
              action,
              isProductWithoutNominal: true,
            },
          });

          return pageDadaResponse.data;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async changeProductWeightPrice(detailPage, payload) {
        try {
          loader.start();
          const resultsStore = useResultsStore();

          const action = this.routes.changeProductWeightPrice?.['params-example-data']?.action;

          const pageDadaResponse = await axios.get(detailPage, {
            params: {
              api: 'Y',
              ...resultsStore.getRequestLang,
              ...payload,
              action,
              isProductWithCustomWeight: true,
            },
          });

          return pageDadaResponse.data;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      setData(payload) {
        this.$state = {
          ...payload,
        };
      },
    },
  }
);
